/**
 * moderation-pipeline-reader-report (Phase 5+ Tier 5A 第 2 段) Phase 4:
 * client-player から reader 通報 API を呼び出すための軽量 fetch wrapper。
 *
 * 設計 doctrine:
 * - client-player は core-domain への dep を持たない (= memory `project_client_player_no_core_domain_dep`)、
 *   types は本 file 内で独自 declaration
 * - sibling: scene-summary-editorial.ts と同 pattern (= manual registration endpoint、 openapi-fetch 外)
 */
import { ApiError, type ProblemDetail } from "./client";

// ─── 独自 type declaration (= core-domain dep なし) ─────────

export type ReportTargetType = "scene" | "scenario" | "creator";

export type ReportCategory =
  | "harassment"
  | "sexual"
  | "violence"
  | "hate"
  | "self-harm"
  | "spam"
  | "other";

export const REPORT_CATEGORIES: readonly ReportCategory[] = [
  "harassment",
  "sexual",
  "violence",
  "hate",
  "self-harm",
  "spam",
  "other",
];

export const REPORT_CATEGORY_LABELS: Record<ReportCategory, string> = {
  harassment: "嫌がらせ・いじめ",
  sexual: "性的に不適切な内容",
  violence: "暴力的な内容",
  hate: "差別・ヘイトスピーチ",
  "self-harm": "自傷・自殺の助長",
  spam: "スパム・宣伝",
  other: "その他",
};

export interface SubmitReportResponse {
  id: string;
  status: string;
  reportedAt: string;
}

// ─── client function ─────────

function fallbackProblem(status: number, statusText: string): ProblemDetail {
  return {
    type: "about:blank",
    title: "Request failed",
    status,
    detail: statusText !== "" ? statusText : "Unknown error",
  };
}

export interface NotificationItem {
  id: string;
  type: string;
  relatedReportId: string | null;
  message: string;
  createdAt: string;
  readAt: string | null;
}

export interface ListNotificationsResponse {
  notifications: NotificationItem[];
  total: number;
}

export interface ReportSummary {
  id: string;
  readerId: string;
  targetType: string;
  targetId: string;
  category: string;
  comment: string | null;
  status: string;
  reportedAt: string;
  processedAt: string | null;
}

export interface ListAdminReportsResponse {
  reports: ReportSummary[];
  total: number;
  page: number;
  limit: number;
}

export type DecisionType =
  | "invalid"
  | "warn"
  | "hide"
  | "permanent_ban"
  | "sustained"
  | "overturned";

export const ADMIN_DECISIONS: readonly DecisionType[] = [
  "invalid",
  "warn",
  "hide",
  "permanent_ban",
];

export const ADMIN_DECISION_LABELS: Record<DecisionType, string> = {
  invalid: "通報を無効と判定",
  warn: "警告",
  hide: "非表示化",
  permanent_ban: "永久 BAN",
  sustained: "appeal 棄却 (= 元 decision 維持)",
  overturned: "appeal 受理 (= 元 decision 取消)",
};

/**
 * POST /api/v1/reports — reader が通報を submit する。
 * sessionHook 必須 (= reader=null で 401)、 24 時間以内 同 target 重複は 429。
 */
export async function submitReport(
  baseUrl: string,
  args: {
    targetType: ReportTargetType;
    targetId: string;
    category: ReportCategory;
    comment?: string;
  },
): Promise<SubmitReportResponse> {
  const response = await fetch(`${baseUrl}/reports`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      targetType: args.targetType,
      targetId: args.targetId,
      category: args.category,
      ...(args.comment !== undefined ? { comment: args.comment } : {}),
    }),
  });
  if (!response.ok) {
    let problem: ProblemDetail;
    try {
      problem = (await response.json()) as ProblemDetail;
    } catch {
      problem = fallbackProblem(response.status, response.statusText);
    }
    throw new ApiError(response.status, problem);
  }
  return (await response.json()) as SubmitReportResponse;
}

/**
 * GET /api/v1/notifications — 自分宛 notification 一覧。
 */
export async function listNotifications(
  baseUrl: string,
  args: { unreadOnly?: boolean; page?: number; limit?: number } = {},
): Promise<ListNotificationsResponse> {
  const params = new URLSearchParams();
  if (args.unreadOnly === true) params.set("unreadOnly", "true");
  if (args.page !== undefined) params.set("page", String(args.page));
  if (args.limit !== undefined) params.set("limit", String(args.limit));
  const qs = params.toString();
  const url = `${baseUrl}/notifications${qs.length > 0 ? `?${qs}` : ""}`;
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    let problem: ProblemDetail;
    try {
      problem = (await response.json()) as ProblemDetail;
    } catch {
      problem = fallbackProblem(response.status, response.statusText);
    }
    throw new ApiError(response.status, problem);
  }
  return (await response.json()) as ListNotificationsResponse;
}

/**
 * POST /api/v1/notifications/:id/read — 既読化 (idempotent)。
 */
export async function markNotificationRead(
  baseUrl: string,
  notificationId: string,
): Promise<void> {
  const response = await fetch(
    `${baseUrl}/notifications/${notificationId}/read`,
    { method: "POST", credentials: "include" },
  );
  if (!response.ok && response.status !== 204) {
    let problem: ProblemDetail;
    try {
      problem = (await response.json()) as ProblemDetail;
    } catch {
      problem = fallbackProblem(response.status, response.statusText);
    }
    throw new ApiError(response.status, problem);
  }
}

/**
 * POST /api/v1/reports/:id/appeal — creator が appeal 提出 (= 30 日以内のみ)。
 */
export async function submitAppeal(
  baseUrl: string,
  reportId: string,
  appealDocs: string,
): Promise<void> {
  const response = await fetch(
    `${baseUrl}/reports/${reportId}/appeal`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ appealDocs }),
    },
  );
  if (!response.ok) {
    let problem: ProblemDetail;
    try {
      problem = (await response.json()) as ProblemDetail;
    } catch {
      problem = fallbackProblem(response.status, response.statusText);
    }
    throw new ApiError(response.status, problem);
  }
}

/**
 * GET /api/v1/admin/reports/queue — admin queue。
 */
export async function listAdminQueue(
  baseUrl: string,
  args: { page?: number; limit?: number } = {},
): Promise<ListAdminReportsResponse> {
  const params = new URLSearchParams();
  if (args.page !== undefined) params.set("page", String(args.page));
  if (args.limit !== undefined) params.set("limit", String(args.limit));
  const qs = params.toString();
  const response = await fetch(
    `${baseUrl}/admin/reports/queue${qs.length > 0 ? `?${qs}` : ""}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    let problem: ProblemDetail;
    try {
      problem = (await response.json()) as ProblemDetail;
    } catch {
      problem = fallbackProblem(response.status, response.statusText);
    }
    throw new ApiError(response.status, problem);
  }
  return (await response.json()) as ListAdminReportsResponse;
}

/**
 * POST /api/v1/admin/reports/:id/decision — admin decision。
 */
export async function submitAdminDecision(
  baseUrl: string,
  reportId: string,
  args: { decision: DecisionType; rationale: string },
): Promise<void> {
  const response = await fetch(
    `${baseUrl}/admin/reports/${reportId}/decision`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify(args),
    },
  );
  if (!response.ok) {
    let problem: ProblemDetail;
    try {
      problem = (await response.json()) as ProblemDetail;
    } catch {
      problem = fallbackProblem(response.status, response.statusText);
    }
    throw new ApiError(response.status, problem);
  }
}
